/**
 * Color Shade Generator Utility
 * 
 * Generates a perceptually smooth gradient of shades from a base color
 * using HSL color space for optimal visual transitions
 */

/**
 * Convert hex color to RGB
 * @param {string} hex - Hex color code (e.g., "#FF5733")
 * @returns {object} RGB object with r, g, b properties
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Convert RGB to hex
 * @param {number} r - Red component (0-255)
 * @param {number} g - Green component (0-255)
 * @param {number} b - Blue component (0-255)
 * @returns {string} Hex color code
 */
export const rgbToHex = (r, g, b) => {
  return "#" + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }).join("");
};

/**
 * Convert RGB to HSL
 * @param {number} r - Red component (0-255)
 * @param {number} g - Green component (0-255)
 * @param {number} b - Blue component (0-255)
 * @returns {object} HSL object with h, s, l properties
 */
export const rgbToHsl = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;
  let h = 0;
  const l = (max + min) / 2;
  const s = diff === 0 ? 0 : diff / (1 - Math.abs(2 * l - 1));

  if (diff !== 0) {
    // Use positive modulo so near-red (g < b) does not become h=360
    if (max === r) h = 60 * (((g - b) / diff + 6) % 6);
    else if (max === g) h = 60 * ((b - r) / diff + 2);
    else h = 60 * ((r - g) / diff + 4);
  }
  if (h < 0) h += 360;
  if (h >= 360) h -= 360;

  return {
    h: Math.round(h) % 360,
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

/**
 * Convert HSL to RGB
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {object} RGB object with r, g, b properties
 */
export const hslToRgb = (h, s, l) => {
  // Normalize hue into [0, 360) — Math.round can yield 360 for near-red colors
  h = ((h % 360) + 360) % 360;
  s = s / 100;
  l = l / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;

  if (h >= 0 && h < 60) { r = c; g = x; b = 0; }
  else if (h >= 60 && h < 120) { r = x; g = c; b = 0; }
  else if (h >= 120 && h < 180) { r = 0; g = c; b = x; }
  else if (h >= 180 && h < 240) { r = 0; g = x; b = c; }
  else if (h >= 240 && h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; } // 300–360

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  };
};

/**
 * Generate up to `count` color shades starting from the selected base color.
 * First swatch is always the exact selected color, then darker variants,
 * then lighter variants if needed to fill the count.
 *
 * @param {string} baseColorHex - Base/selected color in hex (e.g., "#FF5733")
 * @param {number} count - Max shades (default 30)
 * @returns {array} Array of hex color codes
 */
export const generateColorShades = (baseColorHex, count = 30) => {
  if (!baseColorHex) return [];

  const rgb = hexToRgb(baseColorHex);
  if (!rgb) return [];

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const baseH = hsl.h;
  const baseS = hsl.s;
  const baseL = hsl.l;

  const total = Math.max(1, Math.min(count, 90));
  const lightnessMin = 5;
  const lightnessMax = 95;

  const shades = [baseColorHex];
  if (total === 1) return shades;

  const remaining = total - 1;
  // Prefer darker first (classic shade ramp), then lighter to fill.
  const darkerSpan = Math.max(0, baseL - lightnessMin);
  const lighterSpan = Math.max(0, lightnessMax - baseL);
  const totalSpan = darkerSpan + lighterSpan || 1;
  let darkerCount = Math.round((remaining * darkerSpan) / totalSpan);
  let lighterCount = remaining - darkerCount;

  if (darkerSpan === 0) {
    darkerCount = 0;
    lighterCount = remaining;
  } else if (lighterSpan === 0) {
    lighterCount = 0;
    darkerCount = remaining;
  }

  for (let i = 1; i <= darkerCount; i++) {
    const t = i / (darkerCount + 1);
    const lightness = baseL - t * darkerSpan;
    const shadeRgb = hslToRgb(baseH, baseS, lightness);
    shades.push(rgbToHex(shadeRgb.r, shadeRgb.g, shadeRgb.b));
  }

  for (let i = 1; i <= lighterCount; i++) {
    const t = i / (lighterCount + 1);
    const lightness = baseL + t * lighterSpan;
    const shadeRgb = hslToRgb(baseH, baseS, lightness);
    shades.push(rgbToHex(shadeRgb.r, shadeRgb.g, shadeRgb.b));
  }

  // Keep order; drop accidental hex duplicates while preserving first = selected
  const unique = [];
  const seen = new Set();
  for (const shade of shades) {
    const key = shade.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(shade);
  }

  return unique.slice(0, total);
};

/**
 * Popup-only: unique neutrals from pure black → max near-black (default #595959).
 * 8-bit RGB only allows (maxChannel + 1) unique grays in this band
 * (e.g. #000000–#595959 → 90 unique). Count above that is capped.
 * @param {number} count
 * @param {number} maxChannel - end gray channel (0x59 = #595959)
 * @returns {string[]}
 */
export const generateBlackToGrayShades = (count = 90, maxChannel = 0x59) => {
  const end = Math.max(0, Math.min(255, Math.round(maxChannel)));
  const maxUnique = end + 1;
  const total = Math.max(2, Math.min(count, maxUnique));
  const shades = [];
  if (total === maxUnique) {
    for (let i = 0; i <= end; i++) {
      shades.push(rgbToHex(i, i, i));
    }
    return shades;
  }
  // Fewer than full band: even steps from 0 → end, still unique when possible
  for (let i = 0; i < total; i++) {
    const v = Math.round((i / (total - 1)) * end);
    shades.push(rgbToHex(v, v, v));
  }
  // Deduplicate while preserving order (rounding can collide on short spans)
  return [...new Set(shades)];
};

/**
 * Popup-only: unique neutrals from pure white → mirrored near-white band.
 * Default end matches black band depth: 255 - 0x59 = #A6A6A6.
 * @param {number} count
 * @param {number} minChannel - lightest end gray (default 255 - 0x59)
 * @returns {string[]}
 */
export const generateWhiteToGrayShades = (count = 90, minChannel = 255 - 0x59) => {
  const start = 255;
  const end = Math.max(0, Math.min(255, Math.round(minChannel)));
  const maxUnique = start - end + 1;
  const total = Math.max(2, Math.min(count, maxUnique));
  const shades = [];
  if (total === maxUnique) {
    for (let i = start; i >= end; i--) {
      shades.push(rgbToHex(i, i, i));
    }
    return shades;
  }
  for (let i = 0; i < total; i++) {
    const v = Math.round(start - (i / (total - 1)) * (start - end));
    shades.push(rgbToHex(v, v, v));
  }
  return [...new Set(shades)];
};

/**
 * Debounced shade generation for performance
 * Prevents rapid re-generation when color changes quickly
 * 
 * @param {string} baseColorHex - Base color in hex format
 * @param {number} delayMs - Delay in milliseconds (default: 300ms)
 * @returns {Promise} Resolves with array of shades
 */
export const generateColorShadesAsync = (baseColorHex, delayMs = 300) => {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      const shades = generateColorShades(baseColorHex);
      resolve(shades);
    }, delayMs);
    
    return () => clearTimeout(timeout);
  });
};
